import { api } from "@/lib/api/client";

/** The doorbell chime's values for one siren (the backend's `siren` kind settings). Unset = the siren's own default. */
export interface ChimeValues {
  doorbell_chime?: boolean;
  doorbell_sound?: string;
  doorbell_volume?: string;
  doorbell_seconds?: number;
}

/** A field descriptor of the siren kind (`iot.kinds`): labels, help, the length's range and default. */
export interface ChimeField {
  key: keyof ChimeValues;
  type: "boolean" | "string" | "number";
  label: string;
  help_text?: string;
  min?: number;
  max?: number;
  unit?: string;
  integer?: boolean;
  default?: number;
  choices_from?: string;
}

/** A siren in a room only the member's company uses (`GET /doorbell/chime/sirens/`). */
export interface ChimeSiren {
  provider: string;
  source_id: number;
  name: string;
  /** "Hütte (22607)". */
  room: string;
  floor: string;
  available: boolean;
  sounding: boolean;
  settings: ChimeValues;
  /** The siren's own sounds and volumes; an empty list = it has none to choose. */
  choices: Partial<Record<"doorbell_sound" | "doorbell_volume", string[]>>;
}

export interface ChimeSirens {
  sirens: ChimeSiren[];
  fields: ChimeField[];
}

const base = (s: Pick<ChimeSiren, "provider" | "source_id">) =>
  `/doorbell/chime/sirens/${encodeURIComponent(s.provider)}/${s.source_id}/`;

/** Company admins only (403 for a member). */
export const doorbellChimeApi = {
  list: () => api.get<ChimeSirens>("/doorbell/chime/sirens/"),
  save: (siren: Pick<ChimeSiren, "provider" | "source_id">, values: ChimeValues) =>
    api.put<{ provider: string; source_id: number; settings: ChimeValues }>(base(siren), { values }),
  /** Plays once with these (unsaved) values; 429 within 10 s of the last play. */
  play: (siren: Pick<ChimeSiren, "provider" | "source_id">, values: ChimeValues) =>
    api.post<{ status: string }>(`${base(siren)}play/`, { values }),
};
